/**
 * Decodifica o JWT e decide qual das 12 apps deve ser carregada
 * baseado em `profile` (índice numérico) e `org_type` (string).
 *
 * Substitui a lógica do esquilo:
 *   - esquilo/app/Http/Service/RedirectService.php::getAppNames()
 *     que faz `manifest[profile][orgType.toLowerCase()]`
 *
 * Espelho inverso de:
 *   - coruja/scripts/build-manifest.mjs::PROFILE_INDEX
 *   - coruja/scripts/build-manifest.mjs::PROFILE_ORG_KEY
 *
 * @module core/session/profile-router
 */

/**
 * Tabela [profileIndex][orgKey] → nome da app (slug no Vite).
 *
 * Os profileIndex (0-11) vêm do claim `profile` do JWT.
 * Os orgKey ('pub', 'adm', 'cer', 'fmt', 'ban', 'vaq', 'participe')
 * vêm do claim `org_type` do JWT (lowercased).
 *
 * Mantida em sincronia com PROFILE_INDEX/PROFILE_ORG_KEY do
 * scripts/build-manifest.mjs.
 */
const PROFILE_APP_TABLE = {
  0: { pub: 'publico', participe: 'publico' },
  1: { adm: 'adm' },
  2: { cer: 'cerimonial-master', fmt: 'cerimonial-master-fmt' },
  3: { cer: 'cerimonial', fmt: 'cerimonial-fmt' },
  4: { fmt: 'formando' },
  5: { fmt: 'formando-comissao' },
  8: { cer: 'cerimonial-vendedor' },
  10: { fmt: 'formando-desistente' },
  11: { vaq: 'vaquinha', ban: 'bank-master' },
};

/**
 * Decodifica um JWT extraindo o payload (claims). Não verifica
 * assinatura — a verificação acontece no quokka. Aqui só lemos.
 *
 * @param {string} token JWT no formato 'header.payload.signature'.
 * @returns {object|null} payload decodificado ou null se inválido.
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Dada uma combinação profile + orgType, retorna o slug da app
 * que deve ser servida.
 *
 * @param {number|string} profile índice do perfil (0-11).
 * @param {string} orgType tipo de org ('adm', 'fmt', 'cer', etc).
 * @returns {string|null} slug da app ('formando', 'adm', etc) ou null.
 */
export function getAppForProfile(profile, orgType) {
  if (profile === undefined || profile === null) return null;
  if (!orgType) return null;
  const profileRow = PROFILE_APP_TABLE[Number(profile)];
  if (!profileRow) return null;
  return profileRow[String(orgType).toLowerCase()] || null;
}

/**
 * Lê o token, decodifica, e retorna o slug da app target. Conveniência
 * pra ser chamado em uma página root que decide pra onde redirecionar.
 *
 * @param {string} token JWT.
 * @returns {string|null} slug da app ou null se token inválido / sem mapping.
 */
export function getAppFromToken(token) {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return getAppForProfile(payload.profile, payload.org_type);
}
